import React, { useState, useEffect } from 'react';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useTheme } from '@mui/material/styles';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import { useWSContext } from '../../contexts/WebSocketContext';
import { getSupportAgents } from '../../services/suporte-service/supportService';
import FileTypeFilter from '../../components/FileTypeFilter';

function AudioReplyInfo({ url }) {
  const [durationText, setDurationText] = React.useState('--:--');

  React.useEffect(() => {
    if (!url) {
      setDurationText('--:--');
      return undefined;
    }
    const audio = new Audio(url);
    audio.preload = 'metadata';

    const setFromSeconds = (secondsValue) => {
      const seconds = Math.max(0, Math.floor(secondsValue || 0));
      const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
      const ss = String(seconds % 60).padStart(2, '0');
      setDurationText(`${mm}:${ss}`);
    };

    const trySetDuration = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setFromSeconds(audio.duration);
        return;
      }

      const originalTime = audio.currentTime || 0;
      const restore = () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          setFromSeconds(audio.duration);
        }
        audio.currentTime = originalTime;
        audio.removeEventListener('timeupdate', restore);
      };
      audio.currentTime = 1e101;
      audio.addEventListener('timeupdate', restore);
    };

    audio.addEventListener('loadedmetadata', trySetDuration);
    audio.addEventListener('durationchange', trySetDuration);
    audio.load();
    return () => {
      audio.removeEventListener('loadedmetadata', trySetDuration);
      audio.removeEventListener('durationchange', trySetDuration);
      audio.src = '';
    };
  }, [url]);

  return (
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.15 }}>
      {durationText}
    </Typography>
  );
}

function formatAgentName(fullName) {
  const raw = String(fullName || '').trim().replace(/\s+/g, ' ');
  if (!raw) return 'Suporte';
  const parts = raw.split(' ');
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1]}`;
}

function formatMessageTime(value) {
  if (!value) return '';
  const raw = String(value).trim();
  const hhmmss = raw.match(/^(\d{2}):(\d{2})(:\d{2})?$/);
  if (hhmmss) return `${hhmmss[1]}:${hhmmss[2]}`;
  const iso = new Date(raw);
  if (!Number.isNaN(iso.getTime())) {
    const hh = String(iso.getHours()).padStart(2, '0');
    const mm = String(iso.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }
  return raw.slice(0, 5);
}

function getReplyPreview(replyTo) {
  if (!replyTo) return '';
  if (replyTo.msgType === 'file') {
    const rawType =
      (replyTo.msgFileType || `${replyTo.urlFile || ''}`.split('.').pop() || '').toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(rawType)) return 'Foto';
    if (['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(rawType)) return 'Audio';
    if (['mp4', 'mov', '3gpp', 'mkv'].includes(rawType)) return 'Video';
    if (rawType === 'pdf') return 'Documento PDF';
    return 'Arquivo';
  }
  return replyTo.msgText || 'Mensagem';
}

function renderReplyPreview(replyTo) {
  if (!replyTo) return null;
  if (replyTo.msgType !== 'file') {
    return <Typography variant="body2" sx={{ color: 'text.primary' }}>{replyTo.msgText || 'Mensagem'}</Typography>;
  }

  const fileType =
    (replyTo.msgFileType || `${replyTo.urlFile || ''}`.split('.').pop() || '').toLowerCase();
  const url = replyTo.urlFile || '';
  const label = getReplyPreview(replyTo);
  const commonThumbSx = {
    width: 44,
    height: 44,
    borderRadius: 1,
    objectFit: 'cover',
    border: '1px solid rgba(0,0,0,0.12)',
    bgcolor: 'background.paper',
  };

  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(fileType) && url) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="img" src={url} alt="midia" sx={commonThumbSx} />
        <Typography variant="body2" sx={{ color: 'text.primary' }}>{label}</Typography>
      </Box>
    );
  }
  if (['mp4', 'mov', '3gpp', 'mkv'].includes(fileType) && url) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="video" src={url} muted sx={commonThumbSx} />
        <Typography variant="body2" sx={{ color: 'text.primary' }}>{label}</Typography>
      </Box>
    );
  }
  if (['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(fileType)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GraphicEqIcon fontSize="small" />
        <Box>
          <Typography variant="body2" sx={{ color: 'text.primary' }}>{label}</Typography>
          <AudioReplyInfo url={url} />
        </Box>
      </Box>
    );
  }
  if (fileType === 'pdf') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PictureAsPdfIcon fontSize="small" />
        <Typography variant="body2" sx={{ color: 'text.primary' }}>{label}</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <InsertDriveFileIcon fontSize="small" />
      <Typography variant="body2" sx={{ color: 'text.primary' }}>{label}</Typography>
    </Box>
  );
}

function MensagemSuporte({ message, onReply }) {
  const theme = useTheme();
  const { supportName, supportLeftMessage } = useWSContext();
  const fileType = `${message?.urlFile || ''}`.split('.').pop()?.toLowerCase();
  const isAudioMessage =
    message?.msgType === 'file' && ['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(fileType);

  const [agentes, setAgentes] = useState([]);
  const [avatarError, setAvatarError] = useState(false);

  const [atendenteAtual, setAtendenteAtual] = useState(() => {
    const nome = localStorage.getItem('nomeSuporte');
    const foto = localStorage.getItem('fotoSuporte');
    if (nome && foto) {
      return { nome, foto };
    }
    return null;
  });

  useEffect(() => {
    const fetchAtendentes = async () => {
      try {
        const data = await getSupportAgents();
        setAgentes(data);
      } catch (err) {
        console.error('Erro ao buscar agentes de suporte:', err);
      }
    };

    fetchAtendentes();
  }, []);

  useEffect(() => {
    if (agentes.length > 0 && supportName) {
      const encontrado = agentes.find((agente) => agente.nome.startsWith(supportName));
      if (encontrado) {
        if (encontrado.nome !== atendenteAtual?.nome) {
          setAtendenteAtual(encontrado);
        }
        setAvatarError(false);
      }
    }
  }, [agentes, supportName, atendenteAtual]);

  const nomeDoSuporte = formatAgentName(atendenteAtual?.nome || supportName || 'Suporte');
  const fotoDoSuporte = atendenteAtual?.foto;
  const inicialDoNome = nomeDoSuporte.charAt(0).toUpperCase();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
        <Avatar
          alt={nomeDoSuporte}
          src={!avatarError ? fotoDoSuporte : undefined}
          onError={() => setAvatarError(true)}
          sx={{
            marginRight: 2,
            width: 35,
            height: 35,
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            position: 'relative',
            fontWeight: 'bold',
            fontSize: '1.2rem',
          }}
        >
          {(!fotoDoSuporte || avatarError) && inicialDoNome}
          {supportLeftMessage && (
            <DoNotDisturbIcon
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                fontSize: 16,
                color: 'red',
              }}
            />
          )}
        </Avatar>

        <Stack>
          <Box
            sx={{
              marginRight: { xs: 0, sm: 10 },
              backgroundColor: isAudioMessage ? 'transparent' : theme.palette.background.default,
              padding: isAudioMessage ? 0 : 1,
              borderRadius: isAudioMessage ? 0 : 1,
              wordBreak: 'break-word',
            }}
          >
            {message.replyTo && (
              <Box
                sx={{
                  mb: 1,
                  p: 0.8,
                  borderRadius: 1,
                  border: '1px solid rgba(0,0,0,0.18)',
                  borderLeft: '3px solid rgba(0,0,0,0.35)',
                  bgcolor: 'rgba(0,0,0,0.06)',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Respondendo
                </Typography>
                {renderReplyPreview(message.replyTo)}
              </Box>
            )}
            {message.msgType === 'txt' && message.msgText}
            {message.msgType === 'file' && (
              <FileTypeFilter
                urlFile={message.urlFile}
                tone="incoming"
                onReply={isAudioMessage ? () => onReply?.(message) : null}
              />
            )}
            {!isAudioMessage && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  cursor: 'pointer',
                  mt: 0.6,
                  display: 'block',
                }}
                onClick={() => onReply?.(message)}
              >
                Responder
              </Typography>
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(150, 149, 149, 0.94)',
              paddingTop: 0.5,
            }}
          >
            {formatMessageTime(message.date)}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

export default MensagemSuporte;

